import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { useMeQuery, useUpdateMe } from "@/hooks/use-me";
import { useAuthStore } from "@/stores/auth.store";

const schema = z.object({
	firstName: z.string().min(1, "Prénom requis").max(80),
	lastName: z.string().min(1, "Nom requis").max(80),
	phone: z.string().max(30).optional().or(z.literal("")),
	lang: z.enum(["fr", "en"]),
	timezone: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

export function ProfileIdentityPage() {
	const { data: me } = useMeQuery();
	const setUser = useAuthStore((s) => s.setUser);
	const updateMutation = useUpdateMe();
	const [savedAt, setSavedAt] = useState<Date | null>(null);
	const [error, setError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			firstName: me?.firstName ?? "",
			lastName: me?.lastName ?? "",
			phone: "",
			lang: (me?.lang as "fr" | "en") ?? "fr",
			timezone: me?.timezone ?? "Africa/Douala",
		},
	});

	useEffect(() => {
		if (me) {
			reset({
				firstName: me.firstName,
				lastName: me.lastName,
				phone: "",
				lang: (me.lang as "fr" | "en") ?? "fr",
				timezone: me.timezone ?? "Africa/Douala",
			});
		}
	}, [me, reset]);

	const submit = handleSubmit(async (data) => {
		setError(null);
		try {
			const updated = await updateMutation.mutateAsync({
				firstName: data.firstName,
				lastName: data.lastName,
				phone: data.phone || undefined,
				lang: data.lang,
				timezone: data.timezone,
			});
			setUser(updated);
			setSavedAt(new Date());
		} catch (err) {
			setError(extractErrorMessage(err, "Mise à jour impossible"));
		}
	});

	if (!me) return null;

	return (
		<div className="grid-2e">
			<Card>
				<CardHead title="Identité" sub="Visible par votre équipe" />
				<CardBody>
					<form onSubmit={submit} noValidate>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr",
								gap: 16,
							}}
						>
							<FormRow label="Prénom" required>
								<input
									className="aform-i"
									data-testid="profile-firstname"
									{...register("firstName")}
								/>
								{errors.firstName && (
									<div
										style={{
											color: "var(--a-danger)",
											fontSize: 11,
											marginTop: 4,
										}}
									>
										{errors.firstName.message}
									</div>
								)}
							</FormRow>
							<FormRow label="Nom" required>
								<input
									className="aform-i"
									data-testid="profile-lastname"
									{...register("lastName")}
								/>
								{errors.lastName && (
									<div
										style={{
											color: "var(--a-danger)",
											fontSize: 11,
											marginTop: 4,
										}}
									>
										{errors.lastName.message}
									</div>
								)}
							</FormRow>
						</div>

						<FormRow label="Téléphone" helper="Optionnel · format E.164">
							<input
								className="aform-i"
								placeholder="+237 6 99 12 34 56"
								{...register("phone")}
							/>
						</FormRow>

						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr",
								gap: 16,
							}}
						>
							<FormRow label="Langue" required>
								<select className="aform-i" {...register("lang")}>
									<option value="fr">Français</option>
									<option value="en">English</option>
								</select>
							</FormRow>
							<FormRow label="Fuseau horaire" required>
								<select className="aform-i" {...register("timezone")}>
									<option value="Africa/Douala">Africa/Douala (GMT+1)</option>
									<option value="Europe/Paris">Europe/Paris</option>
									<option value="UTC">UTC</option>
								</select>
							</FormRow>
						</div>

						{error && (
							<div className="auth-alert danger" style={{ marginTop: 8 }}>
								<span className="icon"></span>
								<div>{error}</div>
							</div>
						)}

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								marginTop: 6,
							}}
						>
							{savedAt && (
								<span
									className="muted"
									style={{ fontSize: 12, alignSelf: "center" }}
								>
									Enregistré il y a quelques secondes
								</span>
							)}
							<AdminButton
								variant="primary"
								type="submit"
								disabled={!isDirty || updateMutation.isPending}
								data-testid="profile-save"
							>
								{updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
							</AdminButton>
						</div>
					</form>
				</CardBody>
			</Card>

			<div className="stack">
				<Card>
					<CardHead title="Identifiant interne" />
					<CardBody>
						<div className="info-row">
							<span className="l">Email professionnel</span>
							<span className="v mono">{me.email}</span>
						</div>
						<div className="info-row">
							<span className="l">Rôle principal</span>
							<span className="v">{me.role.label}</span>
						</div>
						<div className="info-row">
							<span className="l">Permissions effectives</span>
							<span className="v tn">
								{me.permissions.length === 1 && me.permissions[0] === "*"
									? "Toutes (Super Admin)"
									: me.permissions.length + " permission(s)"}
							</span>
						</div>
						<div className="info-row" style={{ borderBottom: "none" }}>
							<span className="l">MFA</span>
							<span className="v">
								{me.mfaEnabled ? (
									<span className="tag success no-dot">Activée</span>
								) : (
									<span className="tag warning no-dot">Non configurée</span>
								)}
							</span>
						</div>
					</CardBody>
				</Card>
			</div>
		</div>
	);
}
